// 📄 pages/Clientes/ListarClientes.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { RefreshCw, Users, Plus, Search } from 'lucide-react';
import { listClientesForOrg, Cliente } from '../../services/clienteService.ts';
import { useAuth } from '../../context/AuthContext.tsx';

export default function ListarClientes() {
  const { orgActual } = useAuth();
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [loading, setLoading] = useState(true);
  const [busqueda, setBusqueda] = useState('');
  const [mostrarInactivos, setMostrarInactivos] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!orgActual) { setClientes([]); setLoading(false); return; }
    setLoading(true);
    listClientesForOrg(orgActual.id)
      .then(setClientes)
      .catch((err) => console.error('Error cargando clientes:', err))
      .finally(() => setLoading(false));
  }, [orgActual]);

  const inactivosCount = useMemo(() => clientes.filter(c => !c.activo).length, [clientes]);

  const visibles = useMemo(() => {
    const term = busqueda.trim().toLowerCase();
    return clientes
      .filter(c => mostrarInactivos || c.activo)
      .filter(c => !term || c.name.toLowerCase().includes(term));
  }, [clientes, busqueda, mostrarInactivos]);

  return (
    <div className="p-6 max-w-4xl mx-auto text-white">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-light">Clientes</h2>
          <p className="text-sm text-gray-400 mt-1">Clientes de "{orgActual?.name || 'tu organización'}".</p>
        </div>
        {(orgActual?.role === 'owner' || orgActual?.role === 'admin') && (
          <Link
            to="/clientes/nuevo"
            className="flex items-center gap-2 bg-blue-500 hover:bg-blue-600 px-4 py-2 rounded-lg text-sm"
          >
            <Plus className="w-4 h-4" /> Registrar Cliente
          </Link>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-4 mb-6">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            placeholder="Buscar por nombre..."
            className="w-full bg-white/5 border border-white/10 rounded-lg pl-9 pr-3 py-2 text-sm focus:outline-none focus:border-blue-400 transition-all"
          />
        </div>

        {inactivosCount > 0 && (
          <label className="flex items-center gap-2 text-sm text-gray-400 cursor-pointer whitespace-nowrap">
            <input
              type="checkbox"
              className="accent-blue-500 w-4 h-4"
              checked={mostrarInactivos}
              onChange={(e) => setMostrarInactivos(e.target.checked)}
            />
            Mostrar inactivos ({inactivosCount})
          </label>
        )}
      </div>

      {loading ? (
        <div className="p-10 text-center text-gray-400 flex flex-col items-center gap-3">
          <RefreshCw className="w-6 h-6 animate-spin text-blue-500" />
          <p className="text-sm">Cargando clientes...</p>
        </div>
      ) : visibles.length === 0 ? (
        <div className="p-10 text-center text-gray-500 bg-white/5 border border-white/10 rounded-2xl">
          <Users className="w-8 h-8 mx-auto mb-2 text-gray-600" />
          <p className="text-sm">
            {clientes.length === 0 ? 'Aún no hay clientes registrados.' : 'Ningún cliente coincide con la búsqueda.'}
          </p>
        </div>
      ) : (
        <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden divide-y divide-white/5">
          {visibles.map((c) => (
            <div
              key={c.id}
              onClick={() => navigate(`/clientes/${encodeURIComponent(c.id)}`)}
              className={`p-4 flex items-center justify-between hover:bg-white/[0.03] cursor-pointer transition-all ${
                !c.activo ? 'opacity-60' : ''
              }`}
            >
              <div>
                <div className="flex items-center gap-2">
                  <p className="font-medium">{c.name}</p>
                  {!c.activo && (
                    <span className="text-[10px] bg-gray-700 text-gray-300 border border-white/10 px-2 py-0.5 rounded-full uppercase tracking-wider">
                      Inactivo
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-400">{c.email}</p>
                {c.apps.length > 0 && (
                  <p className="text-xs text-gray-500 mt-1">{c.apps.join(', ')}</p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
